import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Input, Row, Select, Switch, Tooltip } from 'antd';
import { CheckCircleOutlined, CloseOutlined, DownloadOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import axios from 'axios';
import DatePicker from 'components/date-picker';
import { useI18n } from 'hooks/useI18n';

import { getApi } from 'redux/sagas/getApiDataSaga';
import { SERVER_IP } from 'assets/Config';
import { API_STATUS, DATE_FORMAT } from 'constants/app-constants';
import { formQueryStringFromObject } from 'helpers';
import { getCustomerReportLegendItems, getCustomerReportRowClassName } from 'helpers/customer-report-status';
import TableComponent from 'components/table-component';
import HighlightComponent from 'components/HighlightComponent';
import { getDateFormat } from 'services/Utils';
import { AiOutlineSearch } from 'react-icons/ai';
import { setCustomerReportData } from 'redux/reducers/reports/reportActions';

import './CustomerReport.scss';

const { RangePicker } = DatePicker;

const CustomerReport = () => {
	const { t } = useI18n();
	const downloadFormats = [
		{ label: t('reports.download_formats.excel'), value: 'excel' },
		{ label: t('reports.download_formats.csv'), value: 'csv' },
		{ label: t('reports.download_formats.pdf'), value: 'pdf' },
	];
	const [selectedService, setSelectedService] = useState('');
	const [selectedMembership, setSelectedMembership] = useState('');
	const [isExpiredCheck, setIsExpiredCheck] = useState(false);
	const [isInActiveCheck, setIsInActiveCheck] = useState(false);
	const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
	const [format, setFormat] = useState('pdf');
	const [loading, setLoading] = useState(false);
	const [searchKey, setSearchKey] = useState('');
	const [tableData, setTableData] = useState([]);

	const reportRedux = useSelector((state) => state?.reportsRedux);
	const globalRedux = useSelector((state) => state.globalRedux);
	const services = useSelector((state) => state?.serviceRedux?.services || []);
	const memberships = useSelector((state) => state?.membershipRedux?.memberships || []);
	const branchId = useSelector((state) => state?.globalRedux?.selectedBranch?.id);
	const loginRedux = useSelector((state) => state.loginRedux);
	const dispatch = useDispatch();

	const getMembership = useCallback(() => {
		const string = formQueryStringFromObject({
			branchId,
			serviceId: selectedService,
		});
		const url = `${SERVER_IP}api/memberships?${string}`;
		dispatch(getApi('GET_MEMBERSHIPS', url));
	}, [dispatch, branchId, selectedService]);

	useEffect(() => {
		if (selectedService) getMembership();
	}, [selectedService, getMembership]);

	const string = useMemo(
		() =>
			formQueryStringFromObject({
				serviceId: selectedService,
				membershipId: selectedMembership,
				startDate: dateRange?.[0] ? new Date(dateRange[0]) : null,
				endDate: dateRange?.[1] ? new Date(dateRange[1]) : null,
				isExpiredCheck,
				isInActiveCheck,
				branchId,
				format,
			}),
		[selectedService, selectedMembership, dateRange, isExpiredCheck, isInActiveCheck, branchId, format]
	);

	const downloadReport = async () => {
		setLoading(true);

		try {
			const res = await axios.get(`${SERVER_IP}api/customers/getCustomerReport?${string}`, {
				responseType: 'blob',
				headers: { Authorization: `Bearer ${loginRedux.token}` },
			});

			const extMap = { excel: 'xlsx', csv: 'csv', pdf: 'pdf' };
			const extension = extMap[format] || 'csv';

			const blob = new Blob([res.data]);
			const url = window.URL.createObjectURL(blob);

			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `Customer_Report.${extension}`);
			document.body.appendChild(link);
			link.click();
			link.remove();
		} catch (err) {
			console.error('Download failed:', err);
			alert(t('reports.download_failed'));
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		setTableData(reportRedux?.customerReportData);
	}, [reportRedux?.customerReportData]);

	const filteredData = useMemo(() => {
		if (searchKey === '') return tableData;
		
		const searchTerm = searchKey.toLowerCase().trim();
		
		return tableData.filter((data) => {
			const customerName = (data?.customerName || '').toLowerCase();
			const mobile = (data?.mobile || '').toString().toLowerCase();
			const email = (data?.email || '').toLowerCase();
			const serviceName = (data?.service?.serviceName || '').toLowerCase();
			const membershipName = (data?.membership?.membership?.membershipName || '').toLowerCase();
			const gender = (data?.gender || '').toLowerCase();
			
			return (
				customerName.includes(searchTerm) ||
				mobile.includes(searchTerm) ||
				email.includes(searchTerm) ||
				serviceName.includes(searchTerm) ||
				membershipName.includes(searchTerm) ||
				gender.includes(searchTerm)
			);
		});
	}, [tableData, searchKey]);

	useEffect(() => {
		getData();
	}, []);

	const getData = async () => {
		setLoading(true);
		const string = formQueryStringFromObject({
			serviceId: selectedService,
			membershipId: selectedMembership,
			startDate: dateRange?.[0] ? new Date(dateRange[0]) : null,
			endDate: dateRange?.[1] ? new Date(dateRange[1]) : null,
			isExpiredCheck,
			isInActiveCheck,
			branchId,
			format: 'json',
		});

		try {
			dispatch(getApi('GET_CUSTOMER_REPORT', `${SERVER_IP}api/customers/getCustomerReport?${string}`));
		} catch (err) {
			console.error('Download failed:', err);
			alert(t('reports.download_failed'));
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		return () => {
			setTableData([]);
			dispatch(setCustomerReportData([]));
		};
	}, [dispatch]);

	const tableLoading = useMemo(() => globalRedux.apiStatus.GET_CUSTOMER_REPORT === API_STATUS.PENDING, [globalRedux.apiStatus]);
	const legendItems = useMemo(() => getCustomerReportLegendItems(t), [t]);

	const columns = [
		{
			title: t('reports.customer_report.columns.number'),
			dataIndex: 'S.No',
			key: 'id',
			width: 60,
			fixed: 'left',
			render: (value, record, index) => <div>{index + 1}</div>,
		},
		{
			title: t('reports.customer_report.columns.customer_name'),
			dataIndex: 'customerName',
			key: 'customerName',
			width: 180,
			sorter: (a, b) => a?.customerName?.localeCompare(b?.customerName),
			render: (value) => (
				<div style={{ fontWeight: 'bold' }}>
					<HighlightComponent highlightClassName="highlightClass" searchWords={[searchKey]} autoEscape textToHighlight={value} />
				</div>
			),
		},
		{
			title: t('reports.customer_report.columns.mobile'),
			dataIndex: 'mobile',
			key: 'mobile',
			width: 130,
			render: (value) => (
				<div style={{ fontWeight: 'bold' }}>
					<HighlightComponent
						highlightClassName="highlightClass"
						searchWords={[searchKey]}
						autoEscape
						textToHighlight={(value || '').toString()}
					/>
				</div>
			),
		},
		{
			title: t('reports.customer_report.columns.email'),
			dataIndex: 'email',
			key: 'email',
			width: 220,
			render: (value) => (
				<div style={{ fontWeight: 'bold' }}>
					<HighlightComponent
						highlightClassName="highlightClass"
						searchWords={[searchKey]}
						autoEscape
						textToHighlight={(value || '').toString()}
					/>
				</div>
			),
		},
		{
			title: t('reports.customer_report.columns.gender'),
			dataIndex: 'gender',
			key: 'gender',
			width: 100,
			render: (value) => (
				<div style={{ fontWeight: 'bold' }}>
					<HighlightComponent
						highlightClassName="highlightClass"
						searchWords={[searchKey]}
						autoEscape
						textToHighlight={(value || '').toString()}
					/>
				</div>
			),
		},
		{
			title: t('reports.customer_report.columns.age'),
			dataIndex: 'age',
			key: 'age',
			width: 80,
			render: (value) => (
				<div style={{ fontWeight: 'bold' }}>
					<HighlightComponent
						highlightClassName="highlightClass"
						searchWords={[searchKey]}
						autoEscape
						textToHighlight={(value || '').toString()}
					/>
				</div>
			),
		},
		{
			title: t('reports.customer_report.columns.height'),
			dataIndex: 'height',
			key: 'height',
			width: 100,
			render: (value) => (
				<div style={{ fontWeight: 'bold' }}>
					<HighlightComponent
						highlightClassName="highlightClass"
						searchWords={[searchKey]}
						autoEscape
						textToHighlight={`${value || 0} cm`}
					/>
				</div>
			),
		},
		{
			title: t('reports.customer_report.columns.weight'),
			dataIndex: 'weight',
			key: 'weight',
			width: 100,
			render: (value) => (
				<div style={{ fontWeight: 'bold' }}>
					<HighlightComponent
						highlightClassName="highlightClass"
						searchWords={[searchKey]}
						autoEscape
						textToHighlight={`${value || 0} kg`}
					/>
				</div>
			),
		},
		{
			title: t('reports.customer_report.columns.service'),
			dataIndex: 'service',
			key: 'service',
			width: 160,
			render: (value) => (
				<div style={{ fontWeight: 'bold' }}>
					<HighlightComponent
						highlightClassName="highlightClass"
						searchWords={[searchKey]}
						autoEscape
						textToHighlight={(value?.serviceName || '').toString()}
					/>
				</div>
			),
		},
		{
			title: t('reports.customer_report.columns.membership'),
			dataIndex: 'membership',
			key: 'membership',
			width: 180,
			render: (value) => (
				<div style={{ fontWeight: 'bold' }}>
					<HighlightComponent
						highlightClassName="highlightClass"
						searchWords={[searchKey]}
						autoEscape
						textToHighlight={(value?.membership?.membershipName || '').toString()}
					/>
				</div>
			),
		},
		{
			title: t('reports.customer_report.columns.balance'),
			dataIndex: 'balance',
			key: 'balance',
			width: 120,
			render: (value) => (
				<div style={{ fontWeight: 'bold' }}>
					<HighlightComponent
						highlightClassName="highlightClass"
						searchWords={[searchKey]}
						autoEscape
						textToHighlight={(value || 0).toString()}
					/>
				</div>
			),
		},
		{
			title: t('reports.customer_report.columns.joining_date'),
			dataIndex: 'joiningDate',
			key: 'joiningDate',
			width: 150,
			render: (value) => (
				<div style={{ fontWeight: 'bold' }}>
					<HighlightComponent
						highlightClassName="highlightClass"
						searchWords={[searchKey]}
						autoEscape
						textToHighlight={getDateFormat(value, DATE_FORMAT.DD_MM_YYYY)}
					/>
				</div>
			),
		},
		{
			title: t('reports.customer_report.columns.next_billing_date'),
			dataIndex: 'nextBillDate',
			key: 'nextBillDate',
			width: 160,
			fixed: 'right',
			render: (value) => (
				<div style={{ fontWeight: 'bold' }}>
					<HighlightComponent
						highlightClassName="highlightClass"
						searchWords={[searchKey]}
						autoEscape
						textToHighlight={value ? getDateFormat(value, DATE_FORMAT.DD_MM_YYYY) : '-'}
					/>
				</div>
			),
		},
	];

	const clearFilters = () => {
		setSelectedService('');
		setSelectedMembership('');
		setDateRange([dayjs().startOf('month'), dayjs().endOf('month')]);
		setIsExpiredCheck(false);
		setIsInActiveCheck(false);
		setFormat('pdf');
		setSearchKey('');
		setTableData([]);
		dispatch(setCustomerReportData([])); // clear redux too
	};

	return (
		<Row fluid className="py-4 customer-report-page">
			<Col span={24}>
				<section className="customer-report-panel customer-report-panel--filters">
				<Row gutter={[12, 12]} className="customer-report-filters">
					{/* Date Range Picker */}
					<Col sm={12} md={6}>
						<div className="customer-report-field">
							<label>{t('reports.filters.range')}</label>
							<RangePicker
								format={DATE_FORMAT.DD_MM_YYYY}
								style={{ width: '100%' }}
								allowClear={false}
								value={dateRange}
								onChange={(dates) => setDateRange(dates)}
								placeholder={[t('reports.placeholders.start_date'), t('reports.placeholders.end_date')]}
							/>
						</div>
					</Col>

					{/* Service and Membership Selectors */}
					<Col sm={6} md={4}>
						<div className="customer-report-field">
							<label>{t('reports.filters.service')}</label>
						<Select
							showSearch
							allowClear
							placeholder={t('reports.placeholders.select_service')}
							value={selectedService || undefined}
							onChange={setSelectedService}
							style={{ width: '100%' }}
							optionFilterProp="children">
							{services.map((srv) => (
								<Select.Option key={srv.id} value={srv.id}>
									{srv.serviceName}
								</Select.Option>
							))}
						</Select>
							</div>
					</Col>

					<Col sm={6} md={4}>
							<div className="customer-report-field">
								<label>{t('reports.filters.membership')}</label>
						<Select
							showSearch
							allowClear
							disabled={!selectedService}
							placeholder={t('reports.placeholders.select_membership')}
							value={selectedMembership || undefined}
							onChange={setSelectedMembership}
							style={{ width: '100%' }}
							optionFilterProp="children">
							{memberships.map((mb) => (
								<Select.Option key={mb.id} value={mb.id}>
									{mb.membershipName} – ₹{mb.offerPrice || mb.originalPrice}
								</Select.Option>
							))}
						</Select>
							</div>
					</Col>

					<Col sm={6} md={3}>
							<div className="customer-report-field">
								<label>{t('reports.filters.format')}</label>
								<Select placeholder={t('reports.placeholders.select_format')} style={{ width: '100%' }} value={format} onChange={setFormat}>
							{downloadFormats.map((f) => (
								<Select.Option key={f.value} value={f.value}>
									{f.label}
								</Select.Option>
							))}
								</Select>
							</div>
					</Col>

					<Col sm={6} md={3}>
							<div className={`customer-report-toggle-card ${isExpiredCheck ? 'is-enabled' : ''}`}>
								<span>{t('reports.labels.expired') || 'Expired'}</span>
								<Tooltip title={t('reports.tooltip.include_expired_memberships')}>
									<Switch
										checked={isExpiredCheck}
										onChange={setIsExpiredCheck}
										checkedChildren={
											<>
												<CheckCircleOutlined />
											</>
										}
										unCheckedChildren={
											<>
												<CloseOutlined />
											</>
										}
									/>
								</Tooltip>
							</div>
					</Col>

					<Col sm={6} md={3}>
							<div className={`customer-report-toggle-card ${isInActiveCheck ? 'is-enabled' : ''}`}>
								<span>{t('reports.labels.inactive') || 'Inactive'}</span>
								<Tooltip title={t('reports.tooltip.include_inactive_customers')}>
									<Switch
										checked={isInActiveCheck}
										onChange={setIsInActiveCheck}
										checkedChildren={
											<>
												<CheckCircleOutlined />
											</>
										}
										unCheckedChildren={
											<>
												<CloseOutlined />
											</>
										}
									/>
								</Tooltip>
							</div>
					</Col>

						<Col sm={6} md={3} className="customer-report-actions-col">
							<Button type="primary" block loading={loading} onClick={getData}>
							{t('reports.actions.load_data')}
						</Button>
					</Col>
						<Col sm={6} md={3} className="customer-report-actions-col">
						<Button block onClick={clearFilters}>
							{t('common.clear')}
						</Button>
					</Col>
					</Row>
					</section>

				<Row>
					<Col span={24} style={{ paddingTop: 20 }}>
						<TableComponent
							loading={tableLoading}
							className="custom-table report-table"
							style={{ width: '100%' }}
							columns={columns}
							bordered
							scroll={{ x: 'max-content', y: 500 }} // ✅ Horizontal + Vertical scroll
							pagination={false} // ✅ Disable pagination
							rowKey={(record) => record.id}
							rowClassName={(record) => getCustomerReportRowClassName(record?.nextBillDate)}
							dataSource={filteredData}
							title={() => (
								<div className="customer-report-table-toolbar">
									<Row gutter={[16, 16]} justify="space-between" align="middle">
										<Col xs={24} xl={12}>
											<Row gutter={[10, 10]}>
												<Col xs={24}>
													<Row gutter={[10, 10]} align="middle" wrap>
													<Col>
														<Input
															className="customer-report-search"
															placeholder={t('reports.search_placeholder')}
															suffix={<AiOutlineSearch />}
															// style={{ height: '30px' }}
															onChange={({ target: { value } }) => setSearchKey(value)}
														/>
													</Col>
													{Boolean(filteredData?.length) && <Col>
														<Button
															type="primary"
															className="customer-report-download-btn"
															icon={<DownloadOutlined />}
															loading={loading}
															disabled={!Boolean(filteredData?.length)}
															onClick={downloadReport}>
															{t('common.download')}
														</Button>
													</Col>}
													<Col>
														<div className="customer-report-results-chip">
															<strong>{filteredData.length}</strong>
															<span>{filteredData.length === 1 ? t('reports.results.record') : t('reports.results.records')}</span>
														</div>
													</Col>
												</Row>
											</Col>
										</Row>
									</Col>
									<Col xs={24} xl={12}>
										<div className="customer-report-legend">
											<span className="customer-report-legend__title">{t('reports.status_guide')}</span>
											<div className="customer-report-legend__list">
												{legendItems.map((item) => (
													<div key={item.key} className={`customer-report-legend__item ${item.legendClassName}`}>
														<span className="customer-report-legend__dot" />
														<span className="customer-report-legend__label">{item.label}</span>
													</div>
												))}
											</div>
										</div>
									</Col>
									</Row>
								</div>
							)}
						/>
					</Col>
				</Row>
			</Col>
		</Row>
	);
};

export default CustomerReport;
